// src/pages/admin/tabs/MediaTab.jsx
import React from 'react';
import { AdminAPI } from '../../../api/admin';
import { Card, Actions, Btn, Field, Badge } from '../ui';

function fmtBytes(n = 0) {
  if (!n || n <= 0) return '—';
  const k = 1024; const sizes = ['B','KB','MB','GB','TB'];
  const i = Math.floor(Math.log(n) / Math.log(k));
  return `${(n / Math.pow(k,i)).toFixed(i ? 1 : 0)} ${sizes[i]}`;
}

export default function MediaTab(){
  const [items, setItems] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(24);
  const [total, setTotal] = React.useState(0);
  const [q, setQ] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  // модалка деталей
  const [open, setOpen] = React.useState(false);
  const [detail, setDetail] = React.useState(null); // {file: {...}} нормализованный

  // загрузка списка
  const load = React.useCallback(async ()=>{
    setBusy(true); setErr('');
    try{
      const resp = await AdminAPI.listMediaLibrary({
        page, page_size: pageSize, q: q || undefined, mime: 'image/*'
      });
      const list = resp?.media || [];
      setItems(list);
      setTotal(resp?.pagination?.total ?? list.length);
    }catch(e){
      setErr(e.message || 'Не удалось загрузить список');
    }finally{
      setBusy(false);
    }
  }, [page, pageSize, q]);

  React.useEffect(()=>{ load(); }, [load]);
  React.useEffect(()=>{ setPage(1); }, [q]);

  // открыть карточку файла
  const openDetail = async (id)=>{
    setOpen(true);
    setDetail({ file: null, _busy: true, _err: '' });
    try{
      const resp = await AdminAPI.mediaInfo(id);
      setDetail({ file: resp?.file, _busy:false, _err:'' });
    }catch(e){
      setDetail({ file: null, _busy:false, _err: e.message || 'Не удалось получить файл' });
    }
  };

  // удалить файл (из списка или деталей)
  const del = async (id)=>{
    if (!window.confirm('Удалить файл? Если он привязан к объектам — удаление будет отклонено.')) return;
    try{
      await AdminAPI.deleteMedia(id);
      // если удаляли из модалки — закрыть её
      if (detail?.file?.id === id) setOpen(false);
      await load();
    }catch(e){
      alert(e?.payload?.error || e.message || 'Не удалось удалить');
    }
  };

  // обновить метаданные из модалки
  const saveMeta = async ()=>{
    if (!detail?.file?.id) return;
    const f = detail.file;
    try{
      setDetail(d=>({...d, _busy:true, _err:''}));
      const resp = await AdminAPI.updateMedia(f.id, {
        alt_text_ru: f.alt_text_ru || undefined,
        alt_text_en: f.alt_text_en || undefined,
        caption_ru:  f.caption_ru  || undefined,
        caption_en:  f.caption_en  || undefined,
        is_active:   !!f.is_active,
      });
      setDetail({ file: resp.file, _busy:false, _err:'' });
      // обновим запись в списке
      setItems(prev => prev.map(x => x.id === resp.file.id ? resp.file : x));
    }catch(e){
      setDetail(d=>({...d, _busy:false, _err: e.message || 'Не удалось сохранить'}));
    }
  };

  // множественная загрузка
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const onUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let ok=0, fail=0;
    for (const f of files){
      try { await AdminAPI.uploadMedia(f); ok++; } catch { fail++; }
    }
    setUploading(false);
    await load();
    if (fail) alert(`Загружено: ${ok}, ошибок: ${fail}`);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Card>
      <h3>Медиатека</h3>
      {err && <div style={{color:'#e66', marginBottom:8}}>{err}</div>}

      <Actions style={{gap:10, flexWrap:'wrap'}}>
        <Field>Поиск
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Имя / подпись / alt…" />
        </Field>

        <div>
          <Btn disabled={page<=1} onClick={()=>setPage(p=>p-1)}>‹ Назад</Btn>{' '}
          <Btn disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}>Вперёд ›</Btn>{' '}
          <Badge>{page} / {totalPages}</Badge>
        </div>

        <Badge>На странице: <b>{items.length}</b></Badge>
        <Badge>Всего изображений: <b>{total}</b></Badge>

        <div style={{marginLeft:'auto'}}>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            style={{display:'none'}}
            onChange={(e)=>onUpload(Array.from(e.target.files||[]))}
          />
          <Btn onClick={()=>inputRef.current?.click()} disabled={uploading}>
            {uploading ? 'Загрузка…' : 'Добавить файлы'}
          </Btn>
        </div>
      </Actions>

      {busy && <div style={{marginTop:8}}>Загрузка…</div>}

      {!busy && !items.length && (
        <div style={{color:'#777', marginTop:8}}>Нет изображений. Загрузите файлы выше.</div>
      )}

      {!busy && !!items.length && (
        <div style={grid}>
          {items.map(m=>{
            const thumb = m.thumbnail_url || m.url;
            return (
              <div key={m.id} style={{...tile, opacity: m.is_active ? 1 : 0.55}}>
                {thumb ? <img src={thumb} alt="" style={img}/> : <div style={empty}>no preview</div>}
                <div style={{display:'grid', gap:6}}>
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:6}}>
                    <div style={{display:'flex', gap:6, alignItems:'center', flexWrap:'wrap'}}>
                      {!m.is_active && <Badge>скрыто</Badge>}
                      <small style={{color:'#777'}}>#{m.id}</small>
                    </div>
                    <div style={{display:'flex', gap:6}}>
                      <Btn onClick={()=>openDetail(m.id)}>Открыть</Btn>
                      <Btn onClick={()=>del(m.id)}>Удалить</Btn>
                    </div>
                  </div>
                  <div style={{fontSize:12, color:'#555', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                    {m.original_filename || m.filename}
                  </div>
                  <div style={{fontSize:12, color:'#777'}}>
                    {m.mime_type || '—'} • {fmtBytes(m.file_size)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Детали файла */}
      {open && (
        <div style={backdrop} onClick={(e)=>{ if (e.target === e.currentTarget) setOpen(false); }}>
          <div style={modal} onClick={(e)=>e.stopPropagation()}>
            <Card>
              <h3>Файл</h3>
              {detail?._err && <div style={{color:'#e66', marginBottom:8}}>{detail._err}</div>}
              {detail?._busy && <div>Загрузка…</div>}
              {detail?.file && (
                <div style={{display:'grid', gap:12}}>
                  <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
                    <div style={{display:'grid', gap:8}}>
                      <div>
                        <div style={{fontSize:12, opacity:.8, marginBottom:6}}>Превью</div>
                        <img
                          src={detail.file.display_url || detail.file.url || detail.file.thumbnail_url}
                          alt=""
                          style={{maxWidth:'100%', maxHeight:360, objectFit:'contain', borderRadius:10, background:'#f5f5f5'}}
                        />
                      </div>
                      <div style={{display:'grid', gap:4, fontSize:12}}>
                        <div><b>Оригинал:</b> {detail.file.url ? <a href={detail.file.url} target="_blank" rel="noreferrer">открыть</a> : '—'}</div>
                        <div><b>Превью:</b> {detail.file.thumbnail_url ? <a href={detail.file.thumbnail_url} target="_blank" rel="noreferrer">открыть</a> : '—'}</div>
                        <div><b>Display:</b> {detail.file.display_url ? <a href={detail.file.display_url} target="_blank" rel="noreferrer">открыть</a> : '—'}</div>
                      </div>
                    </div>

                    <div style={{display:'grid', gap:10}}>
                      <div style={{display:'flex', gap:8, alignItems:'center', flexWrap:'wrap'}}>
                        <Badge>#{detail.file.id}</Badge>
                        {!detail.file.is_active && <Badge>скрыто</Badge>}
                        <Badge>{detail.file.mime_type || '—'}</Badge>
                        <Badge>{fmtBytes(detail.file.file_size)}</Badge>
                      </div>

                      <Field>Имя файла
                        <input disabled value={detail.file.original_filename || detail.file.filename || ''}/>
                      </Field>

                      <Field>ALT (RU)
                        <input value={detail.file.alt_text_ru || ''} onChange={e=>setDetail(d=>({...d, file:{...d.file, alt_text_ru:e.target.value}}))}/>
                      </Field>
                      <Field>ALT (EN)
                        <input value={detail.file.alt_text_en || ''} onChange={e=>setDetail(d=>({...d, file:{...d.file, alt_text_en:e.target.value}}))}/>
                      </Field>

                      <Field>Подпись (RU)
                        <textarea rows={2} value={detail.file.caption_ru || ''} onChange={e=>setDetail(d=>({...d, file:{...d.file, caption_ru:e.target.value}}))}/>
                      </Field>
                      <Field>Подпись (EN)
                        <textarea rows={2} value={detail.file.caption_en || ''} onChange={e=>setDetail(d=>({...d, file:{...d.file, caption_en:e.target.value}}))}/>
                      </Field>

                      <Field>
                        <label style={{display:'inline-flex', alignItems:'center', gap:8}}>
                          <input
                            type="checkbox"
                            checked={!!detail.file.is_active}
                            onChange={e=>setDetail(d=>({...d, file:{...d.file, is_active:e.target.checked}}))}
                          />
                          Активно
                        </label>
                      </Field>

                      <div style={{fontSize:12, opacity:.75}}>
                        Создан: {detail.file.created_at || '—'}<br/>
                        Обновлён: {detail.file.updated_at || '—'}
                      </div>
                    </div>
                  </div>

                  <Actions style={{justifyContent:'space-between'}}>
                    <div>
                      <Btn onClick={()=>setOpen(false)}>Закрыть</Btn>
                    </div>
                    <div>
                      <Btn onClick={()=>del(detail.file.id)}>Удалить</Btn>{' '}
                      <Btn $kind="primary" onClick={saveMeta} disabled={detail._busy}>Сохранить</Btn>
                    </div>
                  </Actions>
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
    </Card>
  );
}

/* стили */
const grid = { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:12, marginTop:12 };
const tile = { border:'1px solid var(--bdr)', borderRadius:12, padding:8, background:'#fff', display:'grid', gap:8 };
const img  = { width:'100%', height:160, objectFit:'cover', borderRadius:8, background:'#f3f3f3' };
const empty= { display:'grid', placeItems:'center', width:'100%', height:160, background:'#f5f5f5', borderRadius:8, color:'#888' };

const backdrop = { position:'fixed', inset:0, background:'rgba(0,0,0,.35)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' };
const modal = { width:'min(1100px, 96vw)', maxHeight:'92vh', overflow:'auto' };
